import React, { ReactElement, useEffect, useState } from "react";
import stylesDefault from "../../SiteManagement/styles"; //TODO export these further up!
import {
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ssoManagementService from "../../../services/SSOManagementService";
import { ProviderSiteDTO } from "@beable/roster-backend-client-v1";
import { SUBPAGES } from "../constants";
import Paging from "../Common/Paging";
import Warning from '@mui/icons-material/Warning';

const getStyles = stylesDefault.pageStyles;

interface SSOSiteListProps {
  action: "search" | "refresh" | undefined;
  selectedItem?: ProviderSiteDTO;
  setSelectedItem: (item: ProviderSiteDTO) => void;
  searchTerm: string;
}

type ProviderSiteWithWarning = ProviderSiteDTO & { warning?: ReactElement };

const initialProviderAndIdentifierCounter = new Map();

export default function SSOProviderSiteList({
  action,
  selectedItem,
  setSelectedItem,
  searchTerm,
}: SSOSiteListProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const defaultState = SUBPAGES.providerSites.defaultState;

  const [fetchData, setFetchData] = useState(true);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [providerSitesListItems, setProviderSitesListItems] = useState<
    ProviderSiteDTO[]
  >([]);
  const [searchProviderSitesResult, setSearchProviderSitesResult] = useState<
    ProviderSiteDTO[]
  >([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [_providerIdentifierCounter, setProviderIdentifierCounter] = useState<Map<string, number>>(initialProviderAndIdentifierCounter);

  const handlePaging = (newPage: number) => {
    setPage(newPage);
    setFetchData(true);
  };

  useEffect(() => {
    const handleSearch = async () => {
      setNoSearchResults(false);
      setIsloading(true);

      if (searchTerm !== "") {
        const searchResult = await ssoManagementService.searchProviderSites(
          searchTerm
        );

        if (searchResult && searchResult.data.length === 0) {
          setNoSearchResults(true);
        }
        setSearchProviderSitesResult(searchResult?.data ?? []);
      } else {
        setSearchProviderSitesResult([]);
      }
      setIsloading(false);
    };

    if (action === "refresh") {
      setSearchProviderSitesResult([]);
      setFetchData(true);
    }

    if (action === "search") {
      handleSearch();
    }
  }, [action, searchTerm]);

  const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.persist();
    e.stopPropagation();

    const selectedRow = e.currentTarget;
    const searchIsEmpty = searchTerm === "";
    const match = searchIsEmpty
      ? providerSitesListItems.find(
          (item) => item.id.toString() === selectedRow.id
        )
      : searchProviderSitesResult.find(
          (item) => item.id.toString() === selectedRow.id
        );

    if (match && match.id !== selectedItem?.id) {
      setSelectedItem(match);
    } else {
      setSelectedItem(defaultState);
    }
  };

  const mapDuplicateProviderAndIdentifier = (providerSites: ProviderSiteDTO[]) => {
    const counter = new Map<string, number>();

    providerSites.forEach(item => {
      const key = `${item.providerId}|${item.identifier}`;
      const value = counter.get(key);
      counter.set(key, value ? (value + 1) : 1);
    });

    return counter;
  }

  const checkDuplicateProviderAndIdentifier = (providerSites: ProviderSiteWithWarning[]): ProviderSiteWithWarning[] => {
    const providerAndIdentifierMap = mapDuplicateProviderAndIdentifier(providerSites);
    setProviderIdentifierCounter(providerAndIdentifierMap);
    
    providerSites.forEach(providerSite => {
      if(providerSite.active) {
        const key = `${providerSite.providerId}|${providerSite.identifier}`;
        const value = providerAndIdentifierMap.get(key);
        const isDuplicate = value && value > 1;
        
        if(isDuplicate) {
          providerSite.warning =  (
            <Typography variant='subtitle2' >
              {value} Duplicate identifiers for the provider <u>{`${providerSite.provider?.name}`}</u>. 
              This is not recommended as this may result in only one site being able to authenticate.
            </Typography>
          )
        }
      }
    });
    return providerSites;
  }

  useEffect(() => {
    const getProviderSiteItems = async () => {
      setIsloading(true);
      const providerSitesResponse = await ssoManagementService.getProviderSites(
        page
      );
      if (providerSitesResponse) {
        const data = checkDuplicateProviderAndIdentifier(providerSitesResponse.data);        
        setProviderSitesListItems(data);
        setPageCount(providerSitesResponse.pages);
      }
      setIsloading(false);
    };

    if (fetchData) {
      getProviderSiteItems();
      setFetchData(false);
    }
  }, [fetchData, page]);

  const getListItems = (): ProviderSiteWithWarning[] => {
    if (searchProviderSitesResult.length) {
      return searchProviderSitesResult;
    }

    return providerSitesListItems;
  };

  return (
    <Container sx={styles.dfb}>
      {noSearchResults ? (
        <Typography sx={styles.mb2}>
          No search results found.
        </Typography>
      ) : undefined}
      <Paging
        filteredResultsLength={searchProviderSitesResult.length}
        totalPages={pageCount}
        setPage={handlePaging}
      />
      {isLoading ? (
        <CircularProgress color="primary" />
      ) : (
        <TableContainer component={Paper} sx={styles.maxHeight}>
          <Table
            sx={styles.table}
            aria-label="sites data table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell align="left">Site&nbsp;Name</TableCell>
                <TableCell align="left">Provider&nbsp;Name</TableCell>
                <TableCell align="left">Identifier</TableCell>
                <TableCell align="left">Environment</TableCell>
                <TableCell align="left">Active</TableCell>
                <TableCell align="left">Provider&nbsp;Validation</TableCell>
                <TableCell align="left">
                  Provider&nbsp;Validation&nbsp;String
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getListItems()?.map((item) => (
                <TableRow
                  sx={
                    selectedItem?.id.toString() === item.id.toString()
                      ? styles.highlight
                      : undefined
                  }
                  id={item.id.toString()}
                  key={item.id}
                  onClick={handleTableRowClick}
                >
                  <TableCell align="left">{item.siteName}</TableCell>
                  <TableCell align="left">
                    {item.provider?.name ?? "unknown"}
                  </TableCell>
                  <TableCell align="left" >
                    {
                      item.warning ?
                      <Tooltip sx={styles.warningTooltip} title={item.warning} arrow >
                        <Warning sx={{ verticalAlign: 'middle' }} />
                      </Tooltip> 
                      : undefined 
                    }
                    <span>{item.identifier}</span>
                  </TableCell>
                  <TableCell align="left">
                    {item.provider?.environment ?? "unknown"}
                  </TableCell>
                  <TableCell align="left">
                    {item.active ? "YES" : "NO"}
                  </TableCell>
                  <TableCell align="left">{item.providerValidation}</TableCell>
                  <TableCell align="left">
                    {item?.providerValidationString?.length > 30
                      ? item?.providerValidationString?.substring(0, 30) + "..."
                      : item?.providerValidationString}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
